import { SnackbarPresenter } from '@daily/shared/components/Snackbar';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useMediaQuery } from '@daily/shared/hooks/useMediaQuery';
import {
  useLocalSessionId,
  useParticipantProperty,
} from '@daily-co/daily-react';
import React, { useRef } from 'react';

import { useRenderBreakoutsInterstitialScreen } from '../../contexts/BreakoutsProvider';
import { useWaitingRoom } from '../../contexts/WaitingRoomProvider';
import { useCallConfig } from '../../hooks/useCallConfig';
import { MOBILE_SIDEBAR_WIDTH } from '../../lib/constants';
import { useSidebarView } from '../../lib/state/core';
import { BreakoutHostHelpNotification } from '../BreakoutRooms/BreakoutHostHelpNotification';
import { useBreakoutSessionState } from '../BreakoutRooms/hooks/useBreakoutSessionState';
import { AudioTracks } from '../Call/AudioTracks';
import { Modals } from '../Call/Modals';
import { PoweredByDailyBanner } from '../PoweredByDailyBanner';
import { Sidebar } from '../Sidebar';
import { WaitingRoomModal, WaitingRoomNotification } from '../WaitingRoom';
import { MobileCall } from './MobileCall';

export const MobileView: React.FC<React.PropsWithChildren<unknown>> = () => {
  const mobileRef = useRef<HTMLDivElement>(null);
  const { poweredByDaily } = useCallConfig();
  const { setShowModal, showModal } = useWaitingRoom();
  const localSessionId = useLocalSessionId();
  const isLocalOwner = useParticipantProperty(localSessionId, 'owner');
  const isJoiningBreakout = useRenderBreakoutsInterstitialScreen();
  const { mediaQueries } = useTheme();
  const { breakoutHost } = useBreakoutSessionState();

  const handleModalClose = () => setShowModal(false);
  const [sideBarView] = useSidebarView();
  const sideBarIsOpen = sideBarView !== null;
  const isLargeScreen = useMediaQuery(mediaQueries.large);

  /* The mobile sidebar is rendered on big enough screens on the side, so we need to take that into account here. */
  const addExtraSpaceForSnackbars =
    isLargeScreen && sideBarIsOpen && !isJoiningBreakout;

  return (
    <div ref={mobileRef} className="mobile">
      {poweredByDaily && <PoweredByDailyBanner />}
      <main>
        <MobileCall />
        <Sidebar />
      </main>
      <AudioTracks />
      <Modals />
      <SnackbarPresenter
        style={{
          right: `${
            addExtraSpaceForSnackbars ? Number(MOBILE_SIDEBAR_WIDTH) + 8 : 8
          }px`,
        }}
      />
      {isLocalOwner && (
        <>
          <WaitingRoomNotification />
          {showModal && <WaitingRoomModal onClose={handleModalClose} />}
        </>
      )}
      {breakoutHost === localSessionId && <BreakoutHostHelpNotification />}
      <style jsx>{`
        .mobile {
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
          position: relative;
          width: 100%;
        }
        main {
          display: flex;
          height: 100%;
          position: relative;
          width: 100%;
        }
      `}</style>
    </div>
  );
};
